/**
 * What the shell renders, given who is signed in and where they are.
 *
 * Split out from `main.tsx` because it is the one piece of auth that is pure:
 * a two-input decision with three outcomes, worth stating once and testing
 * directly rather than inferring from a rendered tree.
 */

import type { AuthStatus } from "./session.ts";
import { readGrant } from "../screens/session/api.ts";

export type GateView = "loading" | "front-door" | "app";

/** Reads the grant this device holds, if any. Injected so the gate can be stepped. */
export type GrantLookup = (sessionId?: string) => { session_id: string } | null;

/**
 * Paths that work with no account at all.
 *
 * Two, and both are load-bearing.
 *
 * `/join/:code` is where a scanned QR lands on somebody else's phone. That guest
 * has no account, wants no account, and the screen is built so nothing durable
 * is written to the device. Putting the front door in front of it would break
 * the only path in this app that is deliberately anonymous — and it would break
 * it on a borrowed phone, mid-evening, with the owner standing right there.
 *
 * `/session/:id` is where that same guest is sent the instant they claim a seat,
 * so gating it swallowed them one tap past the door: they picked their name, the
 * claim succeeded, and the app asked them to sign up. It is public only for a
 * device actually holding a grant for *that* session — the grant is the same
 * capability the server checks on every read and write from this phone, so the
 * gate now answers with the credential the request will carry rather than with a
 * guess about the URL.
 *
 * Rendering the rating screen at `/join/:code` instead was the other way to fix
 * this, and it was rejected: everything the rating screen navigates to — the
 * results screen, "back to rating", a reload of a shared link — is keyed by
 * session id, so the join code would have had to be threaded through all of it,
 * and `/session/:id` would still have been a signed-out dead end for a guest who
 * simply reloaded the page.
 *
 * What is emphatically NOT done here is making `/session/:id` public outright.
 * The screens read from the store and the server, but a session id is a guessable
 * handle, and a front door that opens for anyone who types one is not a door.
 *
 * The join path is matched on the segment rather than the string so `/joins` and
 * `/join` (with no code, which is a 404 by design) cannot slip through.
 */
export function isPublicPath(path: string, held: GrantLookup = readGrant): boolean {
  const segments = path.split("/");
  if (segments.length < 3 || segments[2] === "") return false;
  if (segments[1] === "join") return segments.length === 3;
  if (segments[1] !== "session") return false;
  // Every screen under the session id, not just the rating one: the last tap of
  // a QR night is "See the results", and a guest bounced there is bounced at the
  // exact moment the evening pays off. Scoped to the id in the URL, so a grant
  // for last night's table is not a key to tonight's — `readGrant` answers null
  // for a session it does not hold rather than handing back the wrong seat.
  return held(segments[2]) !== null;
}

/**
 * The gate. A public path skips the question entirely — it must render on a
 * phone with no session and no network to resolve one against.
 */
export function chooseView(status: AuthStatus, path: string, held?: GrantLookup): GateView {
  if (isPublicPath(path, held)) return "app";
  if (status === "signed-in") return "app";
  if (status === "loading") return "loading";
  return "front-door";
}
